import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActionState } from 'src/app/enums/action-state.enum';
import { CoreEnum } from 'src/app/enums/core.enum';
import { MainRoute } from 'src/app/models/MainRoute';
import { StationsRepo } from 'src/app/repositories/stations/repo/station-repo';
import { AlertService } from 'src/app/services';
import { RoutingService } from 'src/app/services/Routing.service';
import { StationService } from 'src/app/services/station.service';
import {
  AlertParameters,
  AlertType,
  GeoGouvAddress,
} from 'src/app/shared/models/models-index';
import { environment } from 'src/environments/environment';
import {
  AccessType,
  ApplicationFeature,
  Energy,
  EnergyType,
  Localisation,
  PaymentType,
  ServiceType,
  Station,
  StationCreate,
  StationUpdate,
  VehicleType,
  Visibility,
} from 'src/swagger-gen/web-secured/models';
import { CreateStationSteps } from '../../models/station/create-station-steps';
import { DetailCheckboxType } from '../../models/station/detail-checkbox-type';
import { ModalDeleteStationComponent } from './modal-delete-station/modal-delete-station.component';
@Component({
    selector: 'app-detail-station',
    templateUrl: './detail-station.component.html',
    styleUrls: ['./detail-station.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DetailStationComponent implements OnInit, OnDestroy {
  readonly occupancyWhiteList: ServiceType[] = [ServiceType.IN_USE, ServiceType.IN_USE_WITH_RESTRICTIONS]

  @Input() viewedStation: any;
  @Input() createdUid: string;

  FEATURE = ApplicationFeature;
  emailPattern = '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$';
  detailForm = this.fb.group({
    name: ['', Validators.required],
    code: [null],
    service: [ServiceType.IN_USE, Validators.required],
    lastActivity: [''],
    paymentTypes: [''],
    about: [''],
    email: ['', Validators.pattern(this.emailPattern)],
    phone: [''],
    energies: ['', [Validators.required]],
    locality: ['', Validators.required],
    address: ['', Validators.required],
    localisation: [''],
    access: [null],
    authorizedVehicles: [''],
    visibility: [Visibility.PUBLIC, Validators.required],
    latitude: ['', Validators.required],
    longitude: ['', Validators.required],
    streetNumber: [''],
    postalCode: ['', Validators.required],
    showOccupancy: [false]
  });

  formStatus = new BehaviorSubject(ActionState.READY);
  actionState = ActionState;
  detailCheckboxType = DetailCheckboxType;

  stationEnergyList = Object.values(EnergyType).filter(e => e === EnergyType.ELECTRICITY || e == EnergyType.WATER);
  stationVisibilityList = Object.values(Visibility);
  stationServiceList = Object.values(ServiceType);
  stationPaymentList = Object.values(PaymentType);
  accessTypeList = Object.values(AccessType);
  vehicleTypeList = Object.values(VehicleType);

  station: any;
  vehicleOption = VehicleType;
  selectedVehicles: VehicleType[] = [];
  selectedEnergies: EnergyType[] = [];
  selectedPayment: PaymentType[] = [];
  geoGouvSubject = new BehaviorSubject('');
  successMessage: string;

  subscriptions = new Subscription();
  newEUid: string;
  newEType: string;
  isDetailView = false;
  isEditting = false;
  isUpdateCreatedStation: boolean;
  invalidAddress: boolean;
  addressList: GeoGouvAddress[];
  selectedAddress: GeoGouvAddress;
  deleteSuccess: string;
  displayOccupancy: boolean = false;

  currentRoute: MainRoute;

  get f() {
    return this.detailForm.controls;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: StationsRepo,
    private translate: TranslateService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private stationService: StationService,
    public dialog: MatDialog,
    private logger: NGXLogger
  ) {
    this.currentRoute = RoutingService.getStationRoute();
    this.subscriptions.add(
      this.route.params.subscribe((params: CreateStationSteps) => {
        if (params.uid && params.step === '1') {
          this.isUpdateCreatedStation = true;
        }
      })
    );
  }

  ngOnInit(): void {
    this.checkView();

    this.subscriptions.add(
      this.geoGouvSubject.subscribe((val) => {
        if (val === Validity.INVALID) {
          this.invalidAddress = true;
        } else {
          this.invalidAddress = false;
        }
      })
    );
  }

  /**
   * Check if user's in Creation View or Station Detail View
   */
  checkView() {
    if (this.viewedStation) {
      this.isDetailView = true;
      this.isEditting = false;
      this.detailForm.disable();
      this.patchStationValues(this.viewedStation);
      this.station = this.viewedStation;
      this.displayOccupancy = environment.core.whitelistAuthorizedFeatures.includes(ApplicationFeature.OCCUPANCY) && this.occupancyWhiteList.includes(this.station.service);
    } else if (this.createdUid) {
      this.getCreatedStation(this.createdUid);
    } else {
      this.patchDefaultValues();
    }
  }

  editForm() {
    this.successMessage = null;
    this.isEditting = true;
    this.detailForm.enable();
  }

  cancelEditingForm() {
    this.isEditting = false;
    this.geoGouvSubject.next(Validity.VALID);
    this.detailForm.disable();
    this.patchStationValues(this.station);
  }

  /**
   * Patch station values when in Station View
   * @param station Station
   */
  patchStationValues(station: Station) {
    this.detailForm.patchValue({
      name: station.name || '',
      code: station.code || null,
      service: station.service || '',
      lastActivity: station.lastActivity || '',
      paymentTypes: station.paymentTypes || [],
      about: station.about || '',
      email: station.contact || '',
      phone: station.phone || '',
      energies: this.convertStationEnergies(station.energies),
      address: station.localisation?.address?.streetAddress || '',
      locality: station.localisation?.address?.city || '',
      localisation: station.localisation ? station.localisation : '',
      access: station.access || '',
      authorizedVehicles: station.authorizedVehicles || [],
      visibility: station.visibility || '',
      latitude: station.localisation?.latitude || '',
      longitude: station.localisation?.longitude || '',
      streetNumber: station.localisation?.address?.streetNumber || '',
      postalCode: station.localisation?.address?.postalCode || '',
      showOccupancy: station.showOccupancy || false
    });
    this.selectedEnergies = this.f.energies.value;
    this.selectedVehicles = this.f.authorizedVehicles.value;
    this.selectedPayment = this.f.paymentTypes.value;
  }

  convertStationEnergies(energies: Energy[]) {
    const energyTypes: EnergyType[] = [];
    if (energies?.length > 0) {
      energies.forEach((e) => energyTypes.push(e.type));
    }
    return energyTypes;
  }

  /**
   * Patch default values when in Creation View
   * @param station Station
   */
  patchDefaultValues() {
    this.detailForm.patchValue({
      energies: [EnergyType.ELECTRICITY]
    });
    this.selectedEnergies = this.f.energies.value;
  }

  getCreatedStation(id: string) {
    this.formStatus.next(ActionState.LOADING);
    this.subscriptions.add(
      this.apiService.getStation(id).subscribe({
        next: (res: Station) => {
          this.formStatus.next(ActionState.READY);
          this.station = res;
          this.patchStationValues(this.station);
        },
        error: (error) => {
          this.showAlert(error);
          this.formStatus.next(ActionState.READY);
        },
      })
    );
  }

  /**
   * Get selected address
   * @param address selected address from dropdown
   */
  selectAddress(selectedAddress: GeoGouvAddress) {
    const addressDetail = selectedAddress.properties;
    this.f.address.patchValue(addressDetail.name);
    this.f.locality.patchValue(addressDetail.city);
    this.f.latitude.patchValue(selectedAddress.geometry.coordinates[1]);
    this.f.longitude.patchValue(selectedAddress.geometry.coordinates[0]);
    this.f.streetNumber.patchValue(addressDetail.housenumber);
    this.f.postalCode.patchValue(addressDetail.postcode);
    const localisation: Localisation = {
      longitude: selectedAddress.geometry.coordinates[0],
      latitude: selectedAddress.geometry.coordinates[1],
      address: {
        streetAddress: addressDetail.name,
        postalCode: addressDetail.postcode,
        city: addressDetail.city,
        streetNumber: addressDetail.housenumber,
        additionalAddressDetails: addressDetail.context,
      },
    };
    this.f.localisation.patchValue(localisation);
    this.geoGouvSubject.next(Validity.VALID);
  }

  patchLocalisation() {
    const formValue = this.detailForm.value;
    const localisation: Localisation = {
      longitude: formValue.longitude !== ''  ? parseFloat(formValue.longitude) : null,
      latitude: formValue.latitude !== ''  ? parseFloat(formValue.latitude) : null,
      address: {
        streetAddress: formValue.address ? formValue.address : null,
        postalCode: formValue.postalCode !== ''  ? formValue.postalCode : null,
        city: formValue.locality !== ''  ? formValue.locality : null,
        streetNumber: formValue.streetNumber !== ''  ? formValue.streetNumber : null,
        additionalAddressDetails: formValue.address,
      },
    };
    this.f.localisation.patchValue(localisation);
  }

  onAddressInputChange(val) {
    this.f.localisation.patchValue('');
    this.f.locality.patchValue('');
    // this.geoGouvSubject.next(Validity.INVALID);

    this.patchLocalisation();

    if (val.length > 3) {
      this.subscriptions.add(
        this.apiService
          .getAddress(val)
          .pipe(map((res) => (res = res.features)))
          .subscribe({
            next: (res: GeoGouvAddress[]) => {
              this.addressList = res;
            },
            error: (error) => {
              this.showAlert(error);
            },
          })
      );
    }
  }

  onCheckboxClicked(event: any, type: DetailCheckboxType) {
    if (this.detailForm.disabled) {
      return;
    }
    if (type === DetailCheckboxType.ENERGY) {
      this.buildSelectedEnergies(event);
    } else if (type === DetailCheckboxType.VEHICLE) {
      this.buildSelectedVehicles(event);
    } else if (type === DetailCheckboxType.PAYMENT) {
      this.buildSelectedPayment(event);
    }
  }

  buildSelectedVehicles(event: any): void {
    if (event.target.checked) {
      if (this.selectedVehicles.indexOf(event.target.value) === -1) {
        this.selectedVehicles.push(event.target.value);
        this.f.authorizedVehicles.patchValue(this.selectedVehicles);
      }
    } else {
      if (this.selectedVehicles.indexOf(event.target.value) !== -1) {
        this.selectedVehicles = this.selectedVehicles.filter(
          (ve) => ve !== event.target.value
        );
        this.f.authorizedVehicles.patchValue(this.selectedVehicles);
      }
    }
  }

  buildSelectedEnergies(event: any): void {
    if (event.target.checked) {
      if (this.selectedEnergies.indexOf(event.target.value) === -1) {
        this.selectedEnergies.push(event.target.value);
        this.f.energies.patchValue(this.selectedEnergies);
      }
    } else {
      if (this.selectedEnergies.indexOf(event.target.value) !== -1) {
        this.selectedEnergies = this.selectedEnergies.filter(
          (ve) => ve !== event.target.value
        );
        this.f.energies.patchValue(this.selectedEnergies);
      }
    }
  }

  buildSelectedPayment(event: any): void {
    if (event.target.checked) {
      if (this.selectedPayment.indexOf(event.target.value) === -1) {
        this.selectedPayment.push(event.target.value);
        this.f.paymentTypes.patchValue(this.selectedPayment);
      }
    } else {
      if (this.selectedPayment.indexOf(event.target.value) !== -1) {
        this.selectedPayment = this.selectedPayment.filter(
          (ve) => ve !== event.target.value
        );
        this.f.paymentTypes.patchValue(this.selectedPayment);
      }
    }
  }

  mapFormValues() {
    const formValue = this.detailForm.value;
    formValue.localisation.latitude = formValue.latitude !== ''  ? parseFloat(formValue.latitude) : null;
    formValue.localisation.longitude = formValue.longitude !== ''  ? parseFloat(formValue.longitude) : null;
    formValue.localisation.address.streetNumber = formValue.streetNumber !== ''  ? parseFloat(formValue.streetNumber) : null;
    formValue.localisation.address.postalCode = formValue.postalCode !== ''  ? formValue.postalCode : null;
    formValue.localisation.address.city = formValue.locality !== ''  ? formValue.locality : null;
  }

  /**
   * Submit form
   * @param isSubmitAndConfigure - decides if user want to submit only or submit then go to the configuration
   */
  onSubmitForm(isSubmitAndConfigure: boolean) {
    this.detailForm.markAllAsTouched();

    if (this.detailForm.valid) {
      if (this.detailForm.value.localisation === '') {
        this.invalidAddress = true;
        return;
      }
      this.formStatus.next(ActionState.LOADING);
      this.mapFormValues();
      const formValue = this.detailForm.value;
      const body = this.prepareStationForRequest(formValue);
      // Station creation view and not in editting mode (step 1)
      if (!this.isDetailView && !this.isUpdateCreatedStation) {
        this.subscriptions.add(
          this.apiService.createGenericStation(body).subscribe({
            next: (response) => {
              // User submitted only
              if (!isSubmitAndConfigure) {
                this.successMessage = this.translate.instant(
                  'stations.form.message.success.content'
                );
                this.newEUid = (response.headers.get('location') as string)
                  .split('/')
                  .pop();
                this.formStatus.next(ActionState.SUCCESS);
              } else {
                // User submitted then go to configuration
                this.formStatus.next(ActionState.READY);
                this.newEUid = (response.headers.get('location') as string)
                  .split('/')
                  .pop();
                // Save Created Station to Local Storage
                const createdStation: any = body;
                createdStation.uid = this.newEUid;
                sessionStorage.setItem(
                  CoreEnum.STATION_DETAIL,
                  JSON.stringify(createdStation)
                );
                this.submitAndConfigure(this.newEUid);
              }
            },
            error: (error) => {
              this.showAlert(error);
              this.formStatus.next(ActionState.READY);
            },
          })
        );
      } else {
        // Station detail view or Editting mode in Creation view (step 1)
        const updatedUid = this.station.uid;
        this.subscriptions.add(
          this.apiService.updateStation(updatedUid, body).subscribe({
            next: (response) => {
              if (!isSubmitAndConfigure) {
                this.successMessage = this.translate.instant(
                  'stations.update.success'
                );
                // For Station Detail view
                if (!this.isUpdateCreatedStation) {
                  this.formStatus.next(ActionState.READY);
                  this.isEditting = false;
                  this.detailForm.disable();

                  // Save updated station to localStorage
                  sessionStorage.setItem(
                    CoreEnum.STATION_DETAIL,
                    JSON.stringify(this.updatedStoredStation(body))
                  );
                  this.station = this.updatedStoredStation(body);
                } else {
                  this.formStatus.next(ActionState.SUCCESS);
                }
              } else {
                // User submitted then go to configuration
                this.formStatus.next(ActionState.READY);
                this.submitAndConfigure(updatedUid);
              }
            },
            error: (error) => {
              this.showAlert(error);
              this.formStatus.next(ActionState.READY);
            },
          })
        );
      }
    }
  }

  updatedStoredStation(newStation: any): any {
    let updatedStation = JSON.parse(
      sessionStorage.getItem(CoreEnum.STATION_DETAIL)
    );
    const uid = updatedStation.uid;
    updatedStation = this.prepareStationForRequest(newStation);
    updatedStation.uid = uid;
    return updatedStation;
  }

  prepareStationForRequest(formValue: any): StationCreate | StationUpdate {
    const body = {} as StationCreate | StationUpdate;
    body.name = formValue.name;
    body.code = formValue.code;
    body.service = formValue.service;
    body.contact = formValue.email;
    body.phone = formValue.phone;
    body.visibility = formValue.visibility;
    body.access = formValue.access;
    body.about = formValue.about;
    body.localisation = formValue.localisation;
    body.showOccupancy = formValue.showOccupancy || false;

    if (formValue.paymentTypes.length > 0) {
      body.paymentTypes = formValue.paymentTypes;
    }
    if (formValue.energies.length > 0) {
      body.energies = formValue.energies;
    }
    if (formValue.authorizedVehicles.length > 0) {
      body.authorizedVehicles = formValue.authorizedVehicles;
    }
    return body;
  }

  submitAndConfigure(uid: string) {
    this.apiService.getStation(uid).subscribe({
      next: (res: Station) => {
        const params: CreateStationSteps = {
          step: '2',
          uid: res.uid
        };
        if (res.code) {
          params.code = res.code;
        }
        this.router.navigate([this.currentRoute.path + '/create', params]);
      },
      error: (error) => {
        this.showAlert(error);
        this.formStatus.next(ActionState.READY);
      },
    });
  }

  goToCreatedStation() {
    this.router.navigateByUrl(`stations/${this.newEUid}`);
  }

  /***************************************  DELETE DIALOG  **********************************/
  openDialog(station: Station) {
    const dialogRef = this.dialog.open(ModalDeleteStationComponent, {
      data: station,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.logger.log(`Dialog result: ${result}`);
    });
  }

  showAlert(error: any) {
    const title = this.translate.instant('error.title');
    const alertParams = new AlertParameters(
      false,
      false,
      true,
      false,
      false,
      false,
      true,
      title
    );
    this.alertService.showAlert(error, AlertType.ERROR, alertParams);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}

export enum Validity {
  VALID = 'VALID',
  INVALID = 'INVALID',
}
